// Invite Team (Capability Setup): who from the team is already on Dosen, and in
// which role, then a member of the organization added to the team or someone new
// invited, in one of the team's roles. Read from and saved to the same backend as
// Operations' Update Team (GET /v3/admin/capability/operations?teamIds=,
// POST /v3/admin/capability/teams/{id}/members): an invitee gets the Org Members
// invitation email, and the team and role when they sign up.

import React, { useMemo, useState } from 'react';
import { Box, Dialog, IconButton, InputBase, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiMessage, capabilityApi, type AddMemberInput } from '../../../../api/capability';
import { useAdminAccess } from '../../../../hooks/useAdminAccess';
import { useProgramContext } from '../../../../hooks/useProgramContext';
import { useCapabilityOperations, useCapabilityOrg, useRefreshCapability } from '../../../../queries/useCapability';
import { Bone } from '../../../../components/ui/DashboardSkeletons';
import InfoTip from '../../../../components/ui/InfoTip';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';
import {
  ActionButton,
  BODY,
  BRANDY,
  DANGER,
  DONE,
  FONT,
  INPUT_SX,
  LABEL,
  LINE,
  SCROLL,
  SECTION_TITLE,
  Segmented,
  SelectField,
  TEXT,
  TEXT_2,
  TEXT_3
} from './designSystem';

type AddKind = 'existing' | 'invite';
type Access = 'learner' | 'manager';
const ADD_KIND: Array<{ value: AddKind; label: string }> = [
  { value: 'invite', label: 'Invite New' },
  { value: 'existing', label: 'Select Existing Person' }
];
const ACCESS: Array<{ value: Access; label: string }> = [
  { value: 'learner', label: 'Learner' },
  { value: 'manager', label: 'Team Manager' }
];
const validEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
const cut = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

const InviteTeamDialog: React.FC<{
  open: boolean;
  team: { id: string; name: string };
  onClose: () => void;
  /** Someone was added or invited. */
  onChanged?: () => void;
}> = ({ open, team, onClose, onChanged }) => {
  // A form: the whole screen on a phone, with its close at the top.
  const isMobileApp = useIsMobileApp();
  const org = useCapabilityOrg();
  const refresh = useRefreshCapability();
  const { isPlatformAdmin } = useProgramContext();
  const { access: adminAccess } = useAdminAccess();
  // Only an org admin may make someone a manager of the team (the backend refuses anyone else).
  const mayMakeManager = isPlatformAdmin || Boolean(adminAccess?.orgAdmin);
  const { data, isLoading, isError, error } = useCapabilityOperations([team.id], open);
  const people = useMemo(
    () => (data?.people ?? []).filter((p) => p.teamId === team.id).sort((a, b) => a.name.localeCompare(b.name)),
    [data, team.id]
  );
  // People are invited to a role once its capabilities and levels are confirmed: only those roles are offered.
  const roles = useMemo(() => (data?.roles ?? []).filter((r) => r.teamId === team.id && r.ready), [data, team.id]);
  // Members of the organization not in this team yet, with their email, since names repeat.
  const others = useMemo(() => {
    const here = new Set(people.map((p) => p.id));
    return (data?.others ?? [])
      .filter((o) => !here.has(o.id))
      .map((o) => ({ id: o.id, name: o.email ? `${o.name} · ${o.email}` : o.name, person: o.name }));
  }, [data, people]);

  const [kind, setKind] = useState<AddKind>('invite');
  const [existingId, setExistingId] = useState<string | null>(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [access, setAccess] = useState<Access>('learner');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const who = kind === 'existing' ? Boolean(existingId) : Boolean(first.trim()) && validEmail(email);
  const canAdd = who && Boolean(roleId) && !saving;
  const reason = !who
    ? kind === 'existing'
      ? 'Select a member first.'
      : 'Enter their first name and a valid email first.'
    : 'Choose their role first.';

  const add = async () => {
    if (!roleId) return;
    const roleName = roles.find((r) => r.id === roleId)?.name ?? 'their role';
    const input: AddMemberInput =
      kind === 'existing'
        ? { userId: existingId as string, roleId, access }
        : { firstName: first.trim(), lastName: last.trim(), email: email.trim(), roleId, access };
    const name = kind === 'existing' ? others.find((o) => o.id === existingId)?.person ?? 'They' : first.trim();
    const managing = access === 'manager' ? ', and manages the team' : '';
    setSaving(true);
    setFailure(null);
    setSent(null);
    try {
      await capabilityApi.addMember(team.id, input, org);
      setSent(
        kind === 'existing'
          ? `${name} is in ${team.name} as ${roleName}${managing}.`
          : `Invitation sent to ${email.trim()}. ${name} joins ${team.name} as ${roleName}${managing}, when they sign up.`
      );
      setExistingId(null);
      setFirst('');
      setLast('');
      setEmail('');
      setAccess('learner');
      refresh('admin');
      onChanged?.();
    } catch (err) {
      setFailure(apiMessage(err, 'That could not be saved. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setFailure(null);
    setSent(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobileApp}
      PaperProps={{
        sx: {
          width: 720,
          maxWidth: '94vw',
          bgcolor: '#1C1C1C',
          border: `1px solid ${LINE}`,
          borderRadius: '8px',
          backgroundImage: 'none',
          [MOBILE_SX]: { width: '100%', maxWidth: '100%', m: 0, border: 'none', borderRadius: 0 }
        }
      }}
    >
      <Box
        data-testid="invite-team"
        sx={{ p: '20px', display: 'flex', flexDirection: 'column', gap: '14px', [MOBILE_SX]: { p: '16px' } }}
      >
        {isMobileApp ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '-8px' }}>
            <Typography sx={{ ...SECTION_TITLE, whiteSpace: 'normal', flex: 1, fontSize: 16 }}>
              Invite Team | {team.name}
            </Typography>
            <IconButton aria-label="Close" onClick={close} sx={{ width: 40, height: 40, mr: '-8px', color: TEXT_2 }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        ) : (
          <Typography sx={{ ...SECTION_TITLE, whiteSpace: 'normal' }}>Invite Team | {team.name}</Typography>
        )}

        {/* Anyone else: someone new, or a member of the organization from another team. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Box>
            <Segmented label={`Add to ${team.name}`} options={ADD_KIND} value={kind} onChange={setKind} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
            {kind === 'existing' ? (
              <Box sx={{ width: 260, [MOBILE_SX]: { width: '100%' } }}>
                <Typography sx={{ ...LABEL, mb: '6px' }}>Person*</Typography>
                <SelectField
                  label="Person"
                  placeholder={others.length ? 'Select a person' : 'No one else'}
                  value={existingId}
                  options={others}
                  onChange={setExistingId}
                />
              </Box>
            ) : (
              [
                { label: 'First Name', value: first, set: setFirst, width: 150, required: true },
                { label: 'Last Name', value: last, set: setLast, width: 150, required: false },
                { label: 'Email', value: email, set: setEmail, width: 230, required: true }
              ].map((field) => (
                <Box key={field.label} sx={{ width: field.width, [MOBILE_SX]: { width: '100%' } }}>
                  <Typography sx={{ ...LABEL, mb: '6px' }}>
                    {field.label}
                    {field.required ? '*' : ''}
                  </Typography>
                  <InputBase
                    value={field.value}
                    onChange={(e) => field.set(e.target.value)}
                    inputProps={{ 'aria-label': field.label, maxLength: field.label === 'Email' ? 254 : 80 }}
                    sx={{ ...INPUT_SX, '&.Mui-focused': { borderColor: BRANDY } }}
                  />
                </Box>
              ))
            )}
            <Box sx={{ width: 200, [MOBILE_SX]: { width: '100%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
                <Typography sx={LABEL}>Role*</Typography>
                <InfoTip
                  title="You can invite people to a role once its capabilities and levels are confirmed."
                  placement="bottom-start"
                  sx={{ fontSize: 14 }}
                />
              </Box>
              <SelectField
                label="Role"
                placeholder={roles.length ? 'Select a role' : 'No confirmed roles yet'}
                value={roleId}
                options={roles.map((r) => ({ id: r.id, name: r.name }))}
                onChange={setRoleId}
              />
            </Box>
            {mayMakeManager && (
              <Box>
                <Typography sx={{ ...LABEL, mb: '6px' }}>Access</Typography>
                <Segmented options={ACCESS} value={access} onChange={setAccess} />
              </Box>
            )}
          </Box>
          {failure && (
            <Typography role="alert" sx={{ ...BODY, color: DANGER }}>
              {failure}
            </Typography>
          )}
          {sent && (
            <Typography role="status" sx={{ ...BODY, color: DONE }}>
              {sent}
            </Typography>
          )}
        </Box>

        {/* Who is already here. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', pt: '12px', borderTop: `1px solid ${LINE}` }}>
          <Typography sx={LABEL}>On Dosen</Typography>
          <Box sx={{ ...SCROLL, maxHeight: 220, border: '1px solid #262626', borderRadius: '4px' }}>
            {isLoading ? (
              <Box role="status" aria-label="Loading the team" sx={{ display: 'flex', flexDirection: 'column' }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      p: '8px 12px',
                      borderTop: i > 0 ? '1px solid #262626' : 'none'
                    }}
                  >
                    <Bone width="40%" height={14} radius="4px" />
                    <Box sx={{ flex: 1 }} />
                    <Bone width={120} height={14} radius="4px" />
                  </Box>
                ))}
              </Box>
            ) : isError ? (
              <Typography role="alert" sx={{ ...BODY, p: '10px 12px', color: DANGER }}>
                {apiMessage(error, 'The team could not be loaded.')}
              </Typography>
            ) : people.length === 0 ? (
              <Typography sx={{ ...BODY, p: '10px 12px', color: TEXT_3 }}>
                No one from {team.name} is on Dosen yet.
              </Typography>
            ) : (
              people.map((p, i) => (
                <Box
                  key={p.id}
                  data-testid="invite-team-person"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    p: '8px 12px',
                    borderTop: i > 0 ? '1px solid #262626' : 'none'
                  }}
                >
                  <Typography
                    sx={{
                      ...cut,
                      flex: 1,
                      fontFamily: FONT,
                      fontSize: 13,
                      fontWeight: 600,
                      color: TEXT,
                      [MOBILE_SX]: { whiteSpace: 'normal' }
                    }}
                  >
                    {p.name}
                  </Typography>
                  {p.invitePending && (
                    <Typography sx={{ ...LABEL, flexShrink: 0, color: BRANDY }}>Invite Pending</Typography>
                  )}
                  <Typography
                    sx={{
                      ...cut,
                      flexShrink: 0,
                      maxWidth: 220,
                      fontFamily: FONT,
                      fontSize: 12.5,
                      color: TEXT_2,
                      // A phone: the role whole, wrapping, rather than cut.
                      [MOBILE_SX]: { flexShrink: 1, maxWidth: '45%', whiteSpace: 'normal', textAlign: 'right' }
                    }}
                  >
                    {p.roleName || 'No role yet'}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ActionButton onClick={close}>Cancel</ActionButton>
          <ActionButton primary disabled={!canAdd} reason={saving ? 'Saving…' : reason} onClick={add}>
            {saving ? 'Saving...' : kind === 'existing' ? 'Add To Team' : 'Send Invite'}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default InviteTeamDialog;
